import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import {
  ShieldCheck, Users, Calendar, Image as ImageIcon, CreditCard, Gift,
  RefreshCw, ArrowLeft, KeyRound, CheckCircle2, AlertCircle,
} from "lucide-react";
import SponsoredEventsPanel from "../components/SponsoredEventsPanel";
import PartnersPanel from "../components/PartnersPanel";
import SystemHealthCard from "../components/SystemHealthCard";
import UnclaimedInvitesPanel from "../components/UnclaimedInvitesPanel";
import FxRatesCard from "../components/FxRatesCard";
import EnquiriesPanel from "../components/EnquiriesPanel";
import UploadAnalyticsPanel from "../components/UploadAnalyticsPanel";
import SponsorLogPanel from "../components/SponsorLogPanel";
import ExpiryPredictor from "../components/ExpiryPredictor";
import RenewRequestsPanel from "../components/RenewRequestsPanel";
import TestimonialsAdminPanel from "../components/TestimonialsAdminPanel";

const StatCard = ({ icon: Icon, label, value, sub, testId }) => (
  <div data-testid={testId} className="bg-white rounded-2xl soft-shadow p-5">
    <div className="flex items-center gap-2 text-navy-700/60 text-xs uppercase tracking-wider font-semibold">
      <Icon className="w-4 h-4 text-coral-400" /> {label}
    </div>
    <div className="text-3xl font-bold text-navy-700 mt-2">{value}</div>
    {sub && <div className="text-xs text-navy-700/50 mt-1">{sub}</div>}
  </div>
);

const GoogleOAuthConfig = () => {
  const [status, setStatus] = useState(null);
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/bootstrap/google-oauth").then((r) => setStatus(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setMsg(null);
    try {
      await api.post("/admin/bootstrap/google-oauth", { client_id: clientId, client_secret: clientSecret });
      setMsg({ ok: true, text: "Google Drive credentials saved. Drive sync is now configured." });
      setClientId(""); setClientSecret("");
      load();
    } catch (err) {
      setMsg({ ok: false, text: err?.response?.data?.detail || "Failed to save credentials" });
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-google-oauth-card" className="bg-white rounded-2xl soft-shadow p-6">
      <div className="flex items-center gap-2">
        <KeyRound className="w-5 h-5 text-coral-400" />
        <h2 className="font-semibold text-navy-700 text-lg">Google Drive OAuth keys</h2>
      </div>
      <p className="text-sm text-navy-700/60 mt-1">
        Paste your Google Cloud OAuth Client ID and Secret here to enable Drive sync — no deployment env vars needed.
      </p>
      {status && (
        <div data-testid="google-oauth-status" className={`mt-3 inline-flex items-center gap-2 text-sm rounded-full px-3 py-1.5 ${status.configured ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}`}>
          {status.configured ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {status.configured ? `Configured (source: ${status.source})` : "Not configured"}
        </div>
      )}
      <form onSubmit={save} className="mt-4 space-y-3">
        <input
          value={clientId} onChange={(e) => setClientId(e.target.value)} required
          data-testid="google-client-id-input"
          placeholder="Client ID (…apps.googleusercontent.com)"
          className="w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300 text-sm"
        />
        <input
          value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} required
          type="password" data-testid="google-client-secret-input"
          placeholder="Client Secret (GOCSPX-…)"
          className="w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300 text-sm"
        />
        {msg && (
          <div data-testid="google-oauth-save-msg" className={`text-sm rounded-md px-3 py-2 ${msg.ok ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}>{msg.text}</div>
        )}
        <button disabled={busy} type="submit" data-testid="google-oauth-save-btn" className="bg-coral-300 text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-coral-400 disabled:opacity-60">
          {busy ? "Saving..." : "Save credentials"}
        </button>
      </form>
    </div>
  );
};

const AdminPage = () => {
  const [status, setStatus] = useState(null);
  const [stats, setStats] = useState(null);
  const [statsError, setStatsError] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const s = await api.get("/admin/stats");
      setStats(s.data);
      setStatsError(null);
    } catch (e) {
      const code = e?.response?.status || 0;
      const detail = e?.response?.data?.detail || e?.message || "unknown error";
      setStatsError(`Could not load stats (HTTP ${code || "network"}): ${detail}`);
    }
  }, []);

  const loadStatus = useCallback(async () => {
    try {
      const r = await api.get("/admin/status");
      setStatus(r.data);
      if (r.data.is_admin) {
        // Fire stats fetch independently — never let it prevent the page from rendering.
        fetchStats();
      }
    } catch (e) {
      // Even /admin/status failed — render a friendly banner instead of a blank screen.
      setStatus({ admin_exists: false, is_admin: false, admin_email: null, _fatal: e?.response?.status || "network" });
    }
  }, [fetchStats]);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  const claim = async () => {
    setBusy(true);
    try { await api.post("/admin/claim"); await loadStatus(); } finally { setBusy(false); }
  };

  const refresh = async () => { await fetchStats(); };

  if (!status) return <div className="min-h-[60vh] flex items-center justify-center text-navy-700/60">Loading...</div>;

  return (
    <section className="min-h-[85vh] bg-cream-100 py-12">
      <div className="max-w-6xl mx-auto px-5">
        <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-navy-700/60 hover:text-coral-300 mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </Link>
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <p className="tracking-widest text-coral-300 text-xs font-semibold uppercase">Admin</p>
            <h1 className="font-serif text-navy-700 text-4xl mt-2">App <span className="italic">monitoring</span></h1>
          </div>
          {status.is_admin && (
            <button onClick={refresh} data-testid="admin-refresh-btn" className="inline-flex items-center gap-2 border border-navy-700/20 text-navy-700 px-4 py-2 rounded-full text-sm font-semibold hover:bg-white">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
          )}
        </div>

        {!status.admin_exists && (
          <div data-testid="admin-claim-card" className="bg-white rounded-3xl soft-shadow p-10 text-center max-w-xl mx-auto">
            <ShieldCheck className="w-10 h-10 text-coral-300 mx-auto" />
            <h2 className="font-serif italic text-navy-700 text-2xl mt-4">Claim the admin role</h2>
            <p className="text-navy-700/60 mt-2 text-sm">No admin exists yet. The first account to claim it becomes the app administrator with access to monitoring stats and system settings.</p>
            <button onClick={claim} disabled={busy} data-testid="admin-claim-btn" className="mt-6 bg-coral-300 text-white px-6 py-3 rounded-full font-semibold hover:bg-coral-400 disabled:opacity-60">
              {busy ? "Claiming..." : "Become admin"}
            </button>
          </div>
        )}

        {status.admin_exists && !status.is_admin && (
          <div data-testid="admin-restricted" className="bg-white rounded-3xl soft-shadow p-10 text-center max-w-xl mx-auto">
            <ShieldCheck className="w-10 h-10 text-navy-700/30 mx-auto" />
            <h2 className="font-serif italic text-navy-700 text-2xl mt-4">Restricted area</h2>
            <p className="text-navy-700/60 mt-2 text-sm">This page is only available to the app administrator{status.admin_email ? ` (${status.admin_email})` : ""}.</p>
          </div>
        )}

        {status.is_admin && (
          <div className="space-y-8">
            {statsError && (
              <div data-testid="admin-stats-error" className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm">
                <div className="font-semibold text-amber-800">Admin stats failed to load.</div>
                <div className="text-amber-700/80 mt-1">{statsError}</div>
                <button onClick={refresh} className="mt-3 inline-flex items-center gap-2 border border-amber-400 text-amber-800 px-3 py-1.5 rounded-full text-xs font-semibold hover:bg-amber-100">
                  <RefreshCw className="w-3.5 h-3.5" /> Retry
                </button>
              </div>
            )}

            <SystemHealthCard />

            {stats && (
              <>
                <ExpiryPredictor stats={stats} />
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  <StatCard icon={Users} label="Users" value={stats.totals.users} sub={`+${stats.activity.signups_7d} this week`} testId="stat-users" />
                  <StatCard icon={Calendar} label="Events" value={stats.totals.events} sub={`+${stats.activity.events_7d} this week`} testId="stat-events" />
                  <StatCard icon={ImageIcon} label="Uploads" value={stats.totals.uploads} sub={`+${stats.activity.uploads_7d} this week`} testId="stat-uploads" />
                  <StatCard icon={CreditCard} label="Paid events" value={stats.totals.paid_events} testId="stat-paid-events" />
                  <StatCard icon={Gift} label="Referred sign-ups" value={stats.totals.referred_signups} testId="stat-referrals" />
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div data-testid="admin-revenue-card" className="bg-white rounded-2xl soft-shadow p-5">
                    <h3 className="font-semibold text-navy-700 text-sm uppercase tracking-wider">Revenue (paid)</h3>
                    {stats.revenue.length === 0 ? (
                      <p className="text-sm text-navy-700/50 mt-3">No paid transactions yet.</p>
                    ) : (
                      <ul className="mt-3 space-y-2">
                        {stats.revenue.map((r) => (
                          <li key={r.currency} className="flex justify-between text-sm">
                            <span className="text-navy-700/70">{r.currency} · {r.payments} payment{r.payments === 1 ? "" : "s"}</span>
                            <span className="font-bold text-navy-700">{r.total.toLocaleString()}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="bg-white rounded-2xl soft-shadow p-5">
                    <h3 className="font-semibold text-navy-700 text-sm uppercase tracking-wider">Events by tier</h3>
                    <ul className="mt-3 space-y-2">
                      {Object.entries(stats.events_by_tier).map(([tier, n]) => (
                        <li key={tier} className="flex justify-between text-sm">
                          <span className="text-navy-700/70 capitalize">{tier}</span>
                          <span className="font-bold text-navy-700">{n}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-white rounded-2xl soft-shadow p-5">
                    <h3 className="font-semibold text-navy-700 text-sm uppercase tracking-wider">Uploads by kind</h3>
                    <ul className="mt-3 space-y-2">
                      {Object.entries(stats.uploads_by_kind).map(([kind, n]) => (
                        <li key={kind} className="flex justify-between text-sm">
                          <span className="text-navy-700/70 capitalize">{kind}</span>
                          <span className="font-bold text-navy-700">{n}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div data-testid="admin-recent-users" className="bg-white rounded-2xl soft-shadow p-5">
                    <h3 className="font-semibold text-navy-700 text-sm uppercase tracking-wider mb-3">Recent sign-ups</h3>
                    <div className="divide-y divide-cream-200">
                      {stats.recent_users.map((u) => (
                        <div key={`${u.email}-${u.created_at}`} className="py-2 flex justify-between items-center text-sm">
                          <div className="min-w-0">
                            <div className="font-medium text-navy-700 truncate">{u.name || "—"}</div>
                            <div className="text-navy-700/50 text-xs truncate">{u.email}</div>
                          </div>
                          <span className="text-[10px] uppercase tracking-wider bg-cream-100 rounded px-2 py-1 text-navy-700/60">{u.provider}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div data-testid="admin-recent-events" className="bg-white rounded-2xl soft-shadow p-5">
                    <h3 className="font-semibold text-navy-700 text-sm uppercase tracking-wider mb-3">Recent events</h3>
                    <div className="divide-y divide-cream-200">
                      {stats.recent_events.map((e) => (
                        <div key={`${e.name}-${e.created_at}`} className="py-2 flex justify-between items-center text-sm">
                          <div className="min-w-0">
                            <div className="font-medium text-navy-700 truncate">{e.name}</div>
                            <div className="text-navy-700/50 text-xs capitalize">{e.type}</div>
                          </div>
                          <span className={`text-[10px] uppercase tracking-wider rounded px-2 py-1 ${e.paid ? "bg-green-50 text-green-700" : "bg-cream-100 text-navy-700/60"}`}>{e.tier}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}

            <GoogleOAuthConfig />
            <UploadAnalyticsPanel />
            <FxRatesCard />
            <SponsoredEventsPanel />
            <RenewRequestsPanel />
            <SponsorLogPanel />
            <UnclaimedInvitesPanel />
            <EnquiriesPanel />
            <TestimonialsAdminPanel />
            <PartnersPanel />
          </div>
        )}
      </div>
    </section>
  );
};

export default AdminPage;
